import React, { useState, useEffect, useRef } from 'react';

function FormModal({ title, isOpen, onClose, onSave, initialData = {}, fields = [], onFieldChange }) {
  const [formData, setFormData] = useState({});
  // 🔧 Track l'ouverture précédente pour ne réinitialiser qu'à l'ouverture
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // 🔧 Ne réinitialiser que quand le modal vient de s'ouvrir
      setFormData(initialData || {});
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]); // 🔥 Dépendance SEULEMENT sur isOpen

  if (!isOpen) return null;

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (onFieldChange) {
      onFieldChange(name, value);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const toggleMultiSelect = (fieldName, value) => {
    const currentValues = formData[fieldName] || [];
    const isSelected = currentValues.includes(value);
    const newValues = isSelected
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    handleChange(fieldName, newValues);
  };

  const renderField = (field, idx) => {
    const commonInputStyle = {
      width: '100%',
      background: '#0d1b2a',
      border: '1px solid rgba(255,255,255,.15)',
      borderRadius: 8,
      padding: '9px 12px',
      color: '#fff',
      fontFamily: 'inherit',
      fontSize: 13,
      outline: 'none',
    };

    const labelStyle = {
      fontSize: 10,
      color: '#667',
      textTransform: 'uppercase',
      letterSpacing: '.8px',
      display: 'block',
      marginBottom: 5,
    };

    let inputElement;

    if (field.type === 'multiselect') {
      inputElement = (
        <div style={{ 
          maxHeight: 180, 
          overflowY: 'auto', 
          background: '#0d1b2a', 
          border: '1px solid rgba(255,255,255,.15)', 
          borderRadius: 8, 
          padding: 8 
        }}>
          {field.opts?.length === 0 ? (
            <div style={{ fontSize: 12, color: '#556', padding: '8px 0', textAlign: 'center' }}>
              {field.emptyMessage || 'Aucune option disponible'}
            </div>
          ) : (
            field.opts?.map((opt, i) => {
              const value = typeof opt === 'object' ? String(opt.value) : String(opt);
              const label = typeof opt === 'object' ? opt.label : opt;
              const currentValues = (formData[field.name] || []).map(String);
              const isSelected = currentValues.includes(value);

              return (
                <div 
                  key={i} 
                  onClick={() => toggleMultiSelect(field.name, value)}
                  style={{
                    padding: '7px 10px',
                    cursor: 'pointer',
                    borderRadius: 6,
                    background: isSelected ? 'rgba(201,168,76,.15)' : 'transparent',
                    color: isSelected ? '#C9A84C' : '#aaa',
                    fontSize: 12,
                    marginBottom: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    transition: 'all .15s',
                  }}
                >
                  <span style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    border: `1px solid ${isSelected ? '#C9A84C' : 'rgba(255,255,255,.25)'}`,
                    background: isSelected ? '#C9A84C' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    flexShrink: 0,
                  }}>
                    {isSelected ? '✓' : ''}
                  </span>
                  <span>{label}</span>
                </div>
              );
            })
          )}
          {(formData[field.name] || []).length > 0 && (
            <div style={{ 
              marginTop: 8, 
              padding: '6px 10px', 
              background: 'rgba(201,168,76,.08)', 
              borderRadius: 6,
              fontSize: 11,
              color: '#C9A84C',
            }}>
              {(formData[field.name] || []).length} sélectionné(s)
            </div>
          )}
        </div>
      );
    } else if (field.type === 'select' || field.opts) {
      inputElement = (
        <select
          value={formData[field.name] || ''}
          onChange={e => handleChange(field.name, e.target.value)}
          disabled={field.disabled}
          style={{
            ...commonInputStyle,
            color: field.disabled ? '#556' : '#fff',
            cursor: field.disabled ? 'not-allowed' : 'pointer',
            opacity: field.disabled ? 0.5 : 1,
          }}
        >
          <option value="">{field.placeholder || '-- Sélectionner --'}</option>
          {field.opts?.map((opt, i) => {
            const value = typeof opt === 'object' ? opt.value : opt;
            const label = typeof opt === 'object' ? opt.label : opt;
            return (
              <option key={i} value={value} style={{ background: '#0d1b2a' }}>
                {label}
              </option>
            );
          })}
        </select>
      );
    } else if (field.type === 'number') {
      inputElement = (
        <input
          type="number"
          value={formData[field.name] || ''}
          onChange={e => handleChange(field.name, e.target.value)}
          disabled={field.disabled}
          min={field.min}
          max={field.max}
          style={commonInputStyle}
        />
      );
    } else if (field.type === 'email') {
      inputElement = (
        <input
          type="email"
          value={formData[field.name] || ''}
          onChange={e => handleChange(field.name, e.target.value)}
          disabled={field.disabled}
          style={commonInputStyle}
        />
      );
    } else if (field.type === 'date') {
      inputElement = (
        <div style={{ position: 'relative' }}>
          <input
            type="date"
            value={formData[field.name] || ''}
            onChange={e => handleChange(field.name, e.target.value)}
            disabled={field.disabled}
            min={field.min}
            max={field.max}
            style={{
              ...commonInputStyle,
              colorScheme: 'dark',
            }}
          />
          <span style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#C9A84C',
            fontSize: 14,
            pointerEvents: 'none',
          }}>
            📅
          </span>
        </div>
      );
    } else if (field.type === 'textarea') {
      inputElement = (
        <textarea
          value={formData[field.name] || ''}
          onChange={e => handleChange(field.name, e.target.value)}
          disabled={field.disabled}
          rows={4}
          placeholder={field.placeholder}
          style={{
            ...commonInputStyle,
            resize: 'vertical',
            minHeight: 80,
          }}
        />
      );
    } else {
      inputElement = (
        <input
          type="text"
          value={formData[field.name] || ''}
          onChange={e => handleChange(field.name, e.target.value)}
          disabled={field.disabled}
          placeholder={field.placeholder}
          style={commonInputStyle}
        />
      );
    }

    return (
      <div key={idx} style={{ marginBottom: 16 }}>
        <label style={labelStyle}>
          {field.label}
          {field.required && <span style={{ color: '#ef5350' }}> *</span>}
        </label>
        {inputElement}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#162535', border: '1px solid rgba(255,255,255,.12)', borderRadius: 14, padding: 28, width: '90%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#889', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {fields.map((field, idx) => renderField(field, idx))}

          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={onClose}>Annuler</button>
            <button type="submit" className="btn-gold" style={{ flex: 1 }}>Enregistrer</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FormModal;